import type { GeometryCategory } from "./types";
import { isMemberOf } from "@shared/validation/narrow";
import { PACKAGING_UNIT_PROFILES } from "@shared/products/packaging-impact";
import {
  BULK_BAG_PRESETS,
  EMPTY_BULK_BAG_WEIGHT_LBS,
} from "./bulk-bag-presets";

export const TRUCK_LOADING_PRODUCT_SLUGS = [
  "pallets",
  "plastic-pallets",
  "gaylord-boxes",
  "ibc-totes",
  "metal-drums",
  "plastic-drums",
  "bulk-bags",
  "wood-crates",
  "cardboard-bales",
  "wooden-spools",
  "custom",
] as const;

export type TruckLoadingProductSlug =
  (typeof TRUCK_LOADING_PRODUCT_SLUGS)[number];

export interface UnitLabels {
  singular: string;
  plural: string;
}

export interface DimensionPreset {
  id: string;
  label: string;
  lengthIn: number;
  widthIn: number;
  heightIn: number;
  weightLbs?: number;
  stackable?: boolean;
  unitLabels?: UnitLabels;
}

export interface ProductTypeConfig {
  slug: TruckLoadingProductSlug;
  singularLabel: string;
  pluralLabel: string;
  defaultGeometry: GeometryCategory;
  geometryOptions?: Array<{
    value: GeometryCategory;
    label: string;
  }>;
  presets: Array<DimensionPreset>;
  defaultWeightLbs: number;
  defaultStackable: boolean;
  supportsPalletized: boolean;
  baseColor: string;
}

export const PRODUCT_CONFIGS: Record<
  TruckLoadingProductSlug,
  ProductTypeConfig
> = {
  pallets: {
    slug: "pallets",
    singularLabel: "Pallet",
    pluralLabel: "Pallets",
    defaultGeometry: "stringer-pallet",
    geometryOptions: [
      { value: "stringer-pallet", label: "Stringer" },
      { value: "block-pallet", label: "Block" },
    ],
    presets: [
      {
        id: "gma",
        label: "GMA Standard (48×40)",
        lengthIn: 48,
        widthIn: 40,
        heightIn: 5,
      },
      {
        id: "block-48",
        label: "Block Pallet (48×48)",
        lengthIn: 48,
        widthIn: 48,
        heightIn: 6,
      },
      { id: "42x42", label: "42×42", lengthIn: 42, widthIn: 42, heightIn: 6 },
      { id: "36x36", label: "36×36", lengthIn: 36, widthIn: 36, heightIn: 6 },
      { id: "custom", label: "Custom", lengthIn: 0, widthIn: 0, heightIn: 0 },
    ],
    defaultWeightLbs: PACKAGING_UNIT_PROFILES["wood-pallets"].typicalEmptyWeightLbs,
    defaultStackable: true,
    supportsPalletized: true,
    baseColor: "#c4944a",
  },
  "plastic-pallets": {
    slug: "plastic-pallets",
    singularLabel: "Plastic Pallet",
    pluralLabel: "Plastic Pallets",
    defaultGeometry: "block-pallet",
    presets: [
      { id: "48x40", label: "48×40", lengthIn: 48, widthIn: 40, heightIn: 6 },
      { id: "48x48", label: "48×48", lengthIn: 48, widthIn: 48, heightIn: 6 },
      { id: "42x42", label: "42×42", lengthIn: 42, widthIn: 42, heightIn: 6 },
      { id: "40x40", label: "40×40", lengthIn: 40, widthIn: 40, heightIn: 6 },
      { id: "custom", label: "Custom", lengthIn: 0, widthIn: 0, heightIn: 0 },
    ],
    defaultWeightLbs: PACKAGING_UNIT_PROFILES["plastic-pallets"].typicalEmptyWeightLbs,
    defaultStackable: true,
    supportsPalletized: true,
    baseColor: "#3b82f6",
  },
  "gaylord-boxes": {
    slug: "gaylord-boxes",
    singularLabel: "Gaylord Box",
    pluralLabel: "Gaylord Boxes",
    defaultGeometry: "box",
    presets: [
      {
        id: "48x40x36",
        label: "48×40×36",
        lengthIn: 48,
        widthIn: 40,
        heightIn: 36,
      },
      {
        id: "48x40x48",
        label: "48×40×48",
        lengthIn: 48,
        widthIn: 40,
        heightIn: 48,
      },
      {
        id: "48x40x41",
        label: "48×40×41",
        lengthIn: 48,
        widthIn: 40,
        heightIn: 41,
      },
      {
        id: "48x45x36",
        label: "48×45×36",
        lengthIn: 48,
        widthIn: 45,
        heightIn: 36,
      },
      { id: "custom", label: "Custom", lengthIn: 0, widthIn: 0, heightIn: 0 },
    ],
    defaultWeightLbs: PACKAGING_UNIT_PROFILES["gaylord-boxes"].typicalEmptyWeightLbs,
    defaultStackable: true,
    supportsPalletized: true,
    baseColor: "#a3845b",
  },
  "ibc-totes": {
    slug: "ibc-totes",
    singularLabel: "IBC Tote",
    pluralLabel: "IBC Totes",
    defaultGeometry: "ibc-cage",
    presets: [
      {
        id: "275gal",
        label: "275 Gallon (48×40×46)",
        lengthIn: 48,
        widthIn: 40,
        heightIn: 46,
      },
      {
        id: "330gal",
        label: "330 Gallon (48×40×53)",
        lengthIn: 48,
        widthIn: 40,
        heightIn: 53,
      },
      { id: "custom", label: "Custom", lengthIn: 0, widthIn: 0, heightIn: 0 },
    ],
    defaultWeightLbs: PACKAGING_UNIT_PROFILES["ibc-totes"].typicalEmptyWeightLbs,
    defaultStackable: true,
    supportsPalletized: true,
    baseColor: "#e2e8f0",
  },
  "metal-drums": {
    slug: "metal-drums",
    singularLabel: "Metal Drum",
    pluralLabel: "Metal Drums",
    defaultGeometry: "cylinder",
    presets: [
      {
        id: "55gal",
        label: "55 Gallon (23×23×35)",
        lengthIn: 23,
        widthIn: 23,
        heightIn: 35,
      },
      {
        id: "85gal",
        label: "85 Gallon overpack (27×27×39)",
        lengthIn: 27,
        widthIn: 27,
        heightIn: 39,
      },
      {
        id: "30gal",
        label: "30 Gallon (19×19×30)",
        lengthIn: 19,
        widthIn: 19,
        heightIn: 30,
      },
      { id: "custom", label: "Custom", lengthIn: 0, widthIn: 0, heightIn: 0 },
    ],
    defaultWeightLbs: PACKAGING_UNIT_PROFILES["steel-drums"].typicalEmptyWeightLbs,
    defaultStackable: true,
    supportsPalletized: true,
    baseColor: "#94a3b8",
  },
  "plastic-drums": {
    slug: "plastic-drums",
    singularLabel: "Plastic Drum",
    pluralLabel: "Plastic Drums",
    defaultGeometry: "cylinder",
    presets: [
      {
        id: "55gal",
        label: "55 Gallon (23×23×35)",
        lengthIn: 23,
        widthIn: 23,
        heightIn: 35,
      },
      {
        id: "30gal",
        label: "30 Gallon (19×19×30)",
        lengthIn: 19,
        widthIn: 19,
        heightIn: 30,
      },
      {
        id: "15gal",
        label: "15 Gallon (15×15×26)",
        lengthIn: 15,
        widthIn: 15,
        heightIn: 26,
      },
      { id: "custom", label: "Custom", lengthIn: 0, widthIn: 0, heightIn: 0 },
    ],
    defaultWeightLbs: PACKAGING_UNIT_PROFILES["plastic-drums"].typicalEmptyWeightLbs,
    defaultStackable: true,
    supportsPalletized: true,
    baseColor: "#3b82f6",
  },
  "bulk-bags": {
    slug: "bulk-bags",
    singularLabel: "Bulk Bag",
    pluralLabel: "Bulk Bags",
    defaultGeometry: "bulk-bag",
    presets: BULK_BAG_PRESETS,
    defaultWeightLbs: EMPTY_BULK_BAG_WEIGHT_LBS,
    defaultStackable: true,
    supportsPalletized: true,
    baseColor: "#fefce8",
  },
  "wood-crates": {
    slug: "wood-crates",
    singularLabel: "Wood Crate",
    pluralLabel: "Wood Crates",
    defaultGeometry: "crate",
    presets: [
      {
        id: "48x40x36",
        label: "48×40×36",
        lengthIn: 48,
        widthIn: 40,
        heightIn: 36,
      },
      {
        id: "60x48x48",
        label: "60×48×48",
        lengthIn: 60,
        widthIn: 48,
        heightIn: 48,
      },
      {
        id: "72x48x48",
        label: "72×48×48",
        lengthIn: 72,
        widthIn: 48,
        heightIn: 48,
      },
      { id: "custom", label: "Custom", lengthIn: 0, widthIn: 0, heightIn: 0 },
    ],
    defaultWeightLbs: 80,
    defaultStackable: true,
    supportsPalletized: true,
    baseColor: "#d4a574",
  },
  "cardboard-bales": {
    slug: "cardboard-bales",
    singularLabel: "Cardboard Bale",
    pluralLabel: "Cardboard Bales",
    defaultGeometry: "bale",
    presets: [
      {
        id: "60x30x42",
        label: "60×30×42 (Vertical)",
        lengthIn: 60,
        widthIn: 30,
        heightIn: 42,
      },
      {
        id: "72x30x42",
        label: "72×30×42 (Large)",
        lengthIn: 72,
        widthIn: 30,
        heightIn: 42,
      },
      {
        id: "48x30x42",
        label: "48×30×42 (Small)",
        lengthIn: 48,
        widthIn: 30,
        heightIn: 42,
      },
      { id: "custom", label: "Custom", lengthIn: 0, widthIn: 0, heightIn: 0 },
    ],
    defaultWeightLbs: 1100,
    defaultStackable: true,
    supportsPalletized: true,
    baseColor: "#a08060",
  },
  "wooden-spools": {
    slug: "wooden-spools",
    singularLabel: "Wooden Spool",
    pluralLabel: "Wooden Spools",
    defaultGeometry: "spool",
    presets: [
      {
        id: "48x48x36",
        label: '48" dia × 36" wide',
        lengthIn: 48,
        widthIn: 48,
        heightIn: 36,
      },
      {
        id: "36x36x24",
        label: '36" dia × 24" wide',
        lengthIn: 36,
        widthIn: 36,
        heightIn: 24,
      },
      {
        id: "24x24x18",
        label: '24" dia × 18" wide',
        lengthIn: 24,
        widthIn: 24,
        heightIn: 18,
      },
      { id: "custom", label: "Custom", lengthIn: 0, widthIn: 0, heightIn: 0 },
    ],
    defaultWeightLbs: 60,
    defaultStackable: true,
    supportsPalletized: true,
    baseColor: "#b8956a",
  },
  custom: {
    slug: "custom",
    singularLabel: "Custom Item",
    pluralLabel: "Custom Items",
    defaultGeometry: "box",
    presets: [
      { id: "custom", label: "Custom", lengthIn: 0, widthIn: 0, heightIn: 0 },
    ],
    defaultWeightLbs: 0,
    defaultStackable: true,
    supportsPalletized: true,
    baseColor: "#64748b",
  },
};

export function isValidProductSlug(
  slug: string,
): slug is TruckLoadingProductSlug {
  return isMemberOf(TRUCK_LOADING_PRODUCT_SLUGS, slug);
}
